import { couponUnlockCodesDelete } from "@saleor/coupons/mutations";
import { CouponUnlockCodesDeleteVariables } from "@saleor/coupons/types/CouponUnlockCodesDelete";
import useAppState from "@saleor/hooks/useAppState";
import useNotifier from "@saleor/hooks/useNotifier";
import { useCallback, useState } from "react";
import { useMutation } from "react-apollo";
import { useIntl } from "react-intl";

const useUnlockCodeDeleteApi = (afterDeleteCallback: () => void) => {
  const intl = useIntl();
  const notify = useNotifier();
  const [, dispatchAppState] = useAppState();
  const [isPromptDialogShown, setIsPromptDialogShown] = useState(false);
  const [idsToBeDeleted, setIdsToBeDeleted] = useState([]);

  const [unlockCodesDeleteMutation] = useMutation<
    CouponUnlockCodesDeleteVariables
  >(couponUnlockCodesDelete);

  const onDelete = useCallback(
    (idsToBeDeleted: string[]) => () => {
      setIdsToBeDeleted(idsToBeDeleted);
      setIsPromptDialogShown(true);
    },
    []
  );

  const onPromptProceed = useCallback(() => {
    setIsPromptDialogShown(false);
    dispatchAppState({
      payload: {
        value: true
      },
      type: "displayLoader"
    });
    unlockCodesDeleteMutation({
      variables: {
        idsToBeDeleted
      }
    })
      .then(() => {
        notify({
          status: "success",
          text: intl.formatMessage({
            defaultMessage: "Unlock Code(s) removed successfully."
          })
        });
      })
      .catch(() => {
        notify({
          status: "error",
          text: intl.formatMessage({
            defaultMessage: "An error occurred when removing unlock codes."
          })
        });
      })
      .finally(() => {
        dispatchAppState({
          payload: {
            value: false
          },
          type: "displayLoader"
        });
        afterDeleteCallback();
      });
  }, [idsToBeDeleted, dispatchAppState, afterDeleteCallback]);

  const onPromptCancel = useCallback(() => {
    setIsPromptDialogShown(false);
  }, []);

  return {
    isPromptDialogShown,
    onDelete,
    onPromptProceed,
    onPromptCancel
  };
};

export default useUnlockCodeDeleteApi;
